import { useState, useCallback } from "react"

export type HookReturnType = {
    /**
     * Value
     */
    value: string

    /**
     * Setter function
     */
    setValue: (value: string) => void

    /**
     * Validation error
     */
    validationError: string | undefined

    /**
     * The function to trigger validation, return true if the value is valid
     */
    // TODO: This is a bad API. We should return the validation errors instantly
    // instead. As it works now, you won't get a hold of the errors until the
    // next render loop. Which means `validationError` returns `undefined` if
    // called directly after checking if this call resulted in false.
    validate: () => boolean

    /**
     * This function clears all validation errors
     */
    clearErrors: () => void

    /**
     * Pass this function to the on-blur handler on your input to get automatically run
     * on-blur formatting, assuming a formatter was passed
     */
    onBlur: () => void
}

/**
 * Wraps a value with an on-demand validator
 */
export function useValidatedState(options: {
    value: string
    clearErrorOnChange?: boolean
    validator: (value?: string) => string | undefined
    onBlur?: (value: string) => string
}): HookReturnType {

    const { clearErrorOnChange, validator } = options

    const [value, setValue] = useState<string>(options.value)
    const [error, setError] = useState<string | undefined>()

    const wrappedSetValue = useCallback((value: string) => {

        if (clearErrorOnChange != null && clearErrorOnChange) {
            setError(undefined)
        }

        setValue(value)

    }, [clearErrorOnChange])

    const validate = useCallback(() => {

        const result = validator(value)
        setError(result)

        return (result == null)

    }, [validator, value])

    const clearErrors = () => {
        setError(undefined)
    }

    const onBlur = () => {
        if (options.onBlur) {
            setValue(options.onBlur(value))
        }
    }

    return {
        value,
        setValue: wrappedSetValue,
        validationError: error,
        validate,
        clearErrors,
        onBlur
    }
}
